import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import {
  useSelector,
} from 'react-redux';

import {
  useFormik,
} from 'formik';

import * as Yup from 'yup';

import {
  toast,
} from 'react-toastify';

import Modal from '../../../../components/modal';

import {
  apiEndPoints,
} from '../../../../hooks/remote/apiEndPoints';

// ============================================================
// ENDPOINT MONITORING
// ============================================================

const EndpointMonitoring = () => {
  document.title =
    'Endpoint Monitoring - Cloudvantage';

  const navigate =
    useNavigate();

  const {
    id: projectId,
  } = useParams();

  // ============================================================
  // USER CURRENCY
  // ============================================================

  const currency =
    useSelector(
      (state) =>
        state.user.currency,
    );

  const normalizedCurrency =
    String(
      currency || 'NGN',
    ).toUpperCase();

  const isDollarWallet =
    normalizedCurrency === 'USD' ||
    normalizedCurrency === '$';

  // ============================================================
  // MONITORS
  // ============================================================

  const [
    monitors,
    setMonitors,
  ] = useState([]);

  const [
    monitorsLoading,
    setMonitorsLoading,
  ] = useState(false);

  // ============================================================
  // PLAN
  // ============================================================

  const [
    planDetails,
    setPlanDetails,
  ] = useState(null);

  const [
    planLoading,
    setPlanLoading,
  ] = useState(false);

  // ============================================================
  // CREATE / EDIT MODAL
  // ============================================================

  const [
    createMonitorModal,
    setCreateMonitorModal,
  ] = useState(false);

  const [
    editMonitorModal,
    setEditMonitorModal,
  ] = useState(false);

  const [
    selectedMonitor,
    setSelectedMonitor,
  ] = useState(null);

  const [
    monitorSubmitting,
    setMonitorSubmitting,
  ] = useState(false);

  // ============================================================
  // MANAGE MONITOR MODAL
  // ============================================================

  const [
    manageMonitorModal,
    setManageMonitorModal,
  ] = useState(false);

  // ============================================================
  // DELETE MODAL
  // ============================================================

  const [
    deleteMonitorModal,
    setDeleteMonitorModal,
  ] = useState(false);

  const [
    deleteMonitorId,
    setDeleteMonitorId,
  ] = useState(null);

  const [
    deleteLoading,
    setDeleteLoading,
  ] = useState(false);

  // ============================================================
  // ENABLE / DISABLE
  // ============================================================

  const [
    toggleLoadingId,
    setToggleLoadingId,
  ] = useState(null);

  // ============================================================
  // PLAN CHANGE MODAL
  // ============================================================

  const [
    changePlanModal,
    setChangePlanModal,
  ] = useState(false);

  const [
    selectedPlan,
    setSelectedPlan,
  ] = useState(null);

  const [
    selectedPaymentMode,
    setSelectedPaymentMode,
  ] = useState('monthly');

  const [
    planSubmitting,
    setPlanSubmitting,
  ] = useState(false);

  // ============================================================
  // RESPONSE HELPER
  // ============================================================

  const unwrapResponse = (
    response,
  ) =>
    response?.data?.data ||
    response?.data ||
    response;

  // ============================================================
  // CURRENT PLAN DERIVED VALUES
  // ============================================================

  const currentPlan =
    planDetails?.plan ||
    null;

  const availablePlans =
    Array.isArray(
      planDetails?.available_plans,
    )
      ? [
          ...planDetails.available_plans,
        ].sort(
          (
            first,
            second,
          ) =>
            Number(
              first?.sort_order ||
                0,
            ) -
            Number(
              second?.sort_order ||
                0,
            ),
        )
      : [];

  const endpointsUsed =
    Number(
      planDetails?.endpoints_used ||
        0,
    );

  const endpointLimit =
    Number(
      currentPlan?.max_endpoints ||
        0,
    );

  const endpointsRemaining =
    endpointLimit > 0
      ? Math.max(
          endpointLimit -
            endpointsUsed,
          0,
        )
      : 0;

  const usagePercentage =
    endpointLimit > 0
      ? Math.min(
          (
            endpointsUsed /
            endpointLimit
          ) *
            100,
          100,
        )
      : 0;

  const minimumCheckInterval =
    Number(
      currentPlan
        ?.min_check_interval_seconds ||
        60,
    );

  const hasReachedEndpointLimit =
    endpointLimit > 0 &&
    endpointsUsed >=
      endpointLimit;

  // ============================================================
  // PAYMENT MODE
  // ============================================================

  const currentPaymentMode =
    String(
      planDetails?.payment_mode ||
        'monthly',
    ).toLowerCase() ===
    'yearly'
      ? 'yearly'
      : 'monthly';

  const currentBillingName =
    currentPaymentMode ===
    'yearly'
      ? 'Yearly'
      : 'Monthly';

  // ============================================================
  // CURRENCY FORMATTERS
  // ============================================================

  const formatNaira = (
    value,
  ) =>
    `₦${Number(
      value || 0,
    ).toLocaleString()}`;

  const formatDollar = (
    value,
  ) =>
    `$${Number(
      value || 0,
    ).toLocaleString(
      undefined,
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      },
    )}`;

  const formatPlanPrice = (
    plan,
    paymentMode,
  ) => {
    if (!plan) {
      return isDollarWallet
        ? '$0'
        : '₦0';
    }

    const yearly =
      paymentMode ===
      'yearly';

    if (isDollarWallet) {
      return formatDollar(
        yearly
          ? plan?.yearly_dollar_price
          : plan?.monthly_dollar_price,
      );
    }

    return formatNaira(
      yearly
        ? plan?.yearly_naira_price
        : plan?.monthly_naira_price,
    );
  };

  // ============================================================
  // CURRENT SUBSCRIPTION PRICE
  // ============================================================

  const currentPriceLabel =
    `${currentBillingName} ${
      isDollarWallet
        ? 'USD'
        : 'NGN'
    }`;

  const currentPriceValue =
    formatPlanPrice(
      currentPlan,
      currentPaymentMode,
    );

  // ============================================================
  // FETCH PLAN
  // ============================================================

  const fetchEndpointMonitoringPlan =
    useCallback(async () => {
      if (!projectId) {
        setPlanDetails(null);
        return;
      }

      try {
        setPlanLoading(true);

        const response =
          await apiEndPoints.getEndpointMonitorPlan(
            projectId,
          );

        const data =
          unwrapResponse(
            response,
          );

        console.log(
          'Endpoint Monitoring Plan:',
          data,
        );

        if (
          data?.statuscode &&
          data.statuscode !==
            '00'
        ) {
          toast.error(
            data?.statusmessage ||
              data?.message ||
              'Unable to fetch endpoint monitoring plan',
          );

          setPlanDetails(
            null,
          );

          return;
        }

        setPlanDetails(
          data,
        );
      } catch (error) {
        console.error(
          'Unable to fetch endpoint monitoring plan:',
          error,
        );

        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            'Unable to fetch endpoint monitoring plan',
        );

        setPlanDetails(
          null,
        );
      } finally {
        setPlanLoading(
          false,
        );
      }
    }, [
      projectId,
    ]);

  // ============================================================
  // FETCH MONITORS
  // ============================================================

  const fetchEndpointMonitors =
    useCallback(async () => {
      if (!projectId) {
        setMonitors([]);
        return;
      }

      try {
        setMonitorsLoading(
          true,
        );

        const response =
          await apiEndPoints.listEndpointMonitors(
            projectId,
          );

        const data =
          unwrapResponse(
            response,
          );

        console.log(
          'Endpoint Monitors:',
          data,
        );

        if (
          data?.statuscode &&
          data.statuscode !==
            '00'
        ) {
          toast.error(
            data?.statusmessage ||
              data?.message ||
              'Unable to fetch endpoint monitors',
          );

          setMonitors([]);

          return;
        }

        const monitorList =
          Array.isArray(data)
            ? data
            : Array.isArray(
                  data?.monitors,
                )
              ? data.monitors
              : Array.isArray(
                    data?.results,
                  )
                ? data.results
                : [];

        setMonitors(
          monitorList,
        );
      } catch (error) {
        console.error(
          'Unable to fetch endpoint monitors:',
          error,
        );

        setMonitors([]);

        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            'Unable to fetch endpoint monitors',
        );
      } finally {
        setMonitorsLoading(
          false,
        );
      }
    }, [
      projectId,
    ]);

  // ============================================================
  // INITIAL FETCH
  // ============================================================

  useEffect(() => {
    fetchEndpointMonitoringPlan();
    fetchEndpointMonitors();
  }, [
    fetchEndpointMonitoringPlan,
    fetchEndpointMonitors,
  ]);

  // ============================================================
  // MONITOR FORM
  // ============================================================

  const monitorForm =
    useFormik({
      enableReinitialize: true,

      initialValues: {
        check_type:
          selectedMonitor
            ?.check_type ||
          'http',

        target:
          selectedMonitor
            ?.target ||
          '',

        port:
          selectedMonitor
            ?.port ||
          443,

        expected_status_code:
          selectedMonitor
            ?.expected_status_code ||
          200,

        check_interval_seconds:
          selectedMonitor
            ?.check_interval_seconds ||
          minimumCheckInterval,

        failure_threshold:
          selectedMonitor
            ?.failure_threshold ||
          2,

        notify_email:
          selectedMonitor
            ?.notify_email ||
          '',

        email_ccs:
          selectedMonitor
            ?.email_ccs ||
          '',

        is_enabled:
          selectedMonitor
            ?.is_enabled ??
          true,
      },

      validationSchema:
        Yup.object({
          check_type:
            Yup.string().required(
              'Check type is required',
            ),

          target:
          Yup.string()
            .required(
              'Endpoint URL is required',
            )
            .test(
              'valid-url',
              'Enter a valid URL',
              (value) => {
                if (!value) {
                  return false;
                }

                try {
                  const url =
                    new URL(value);

                  return (
                    url.protocol === 'http:' ||
                    url.protocol === 'https:'
                  );
                } catch {
                  return false;
                }
              },
            ),

          port:
            Yup.number()
              .typeError(
                'Port must be a number',
              )
              .min(
                1,
                'Minimum port is 1',
              )
              .max(
                65535,
                'Maximum port is 65535',
              )
              .required(
                'Port is required',
              ),

          expected_status_code:
            Yup.number()
              .typeError(
                'Expected status code must be a number',
              )
              .min(
                100,
                'Enter a valid HTTP status code',
              )
              .max(
                599,
                'Enter a valid HTTP status code',
              )
              .required(
                'Expected status code is required',
              ),

          check_interval_seconds:
            Yup.number()
              .typeError(
                'Check interval must be a number',
              )
              .min(
                minimumCheckInterval,
                `Minimum interval for your plan is ${minimumCheckInterval} seconds`,
              )
              .required(
                'Check interval is required',
              ),

          failure_threshold:
            Yup.number()
              .typeError(
                'Failure threshold must be a number',
              )
              .min(
                1,
                'Failure threshold must be at least 1',
              )
              .required(
                'Failure threshold is required',
              ),

          notify_email:
            Yup.string()
              .email(
                'Enter a valid email address',
              )
              .nullable(),

          email_ccs:
            Yup.string()
              .email(
                'Enter a valid CC email address',
              )
              .nullable(),
        }),

      onSubmit:
        async (
          values,
        ) => {
          try {
            setMonitorSubmitting(
              true,
            );

            const payload = {
              check_type:
                values.check_type,

              target:
                values.target.trim(),

              port:
                Number(
                  values.port,
                ),

              expected_status_code:
                Number(
                  values.expected_status_code,
                ),

              check_interval_seconds:
                Number(
                  values.check_interval_seconds,
                ),

              failure_threshold:
                Number(
                  values.failure_threshold,
                ),

              notify_email:
                values.notify_email?.trim() ||
                '',

              email_ccs:
                values.email_ccs?.trim() ||
                '',

              is_enabled:
                Boolean(
                  values.is_enabled,
                ),
            };

            let response;

            if (
              selectedMonitor?.id
            ) {
              response =
                await apiEndPoints.updateEndpointMonitor(
                  projectId,
                  selectedMonitor.id,
                  payload,
                );
            } else {
              response =
                await apiEndPoints.createEndpointMonitor(
                  projectId,
                  payload,
                );
            }

            const data =
              unwrapResponse(
                response,
              );

            if (
              data?.statuscode &&
              data.statuscode !==
                '00'
            ) {
              toast.error(
                data?.statusmessage ||
                  data?.message ||
                  'Unable to save endpoint monitor',
              );

              return;
            }

            toast.success(
              selectedMonitor?.id
                ? 'Endpoint monitor updated successfully'
                : 'Endpoint monitor created successfully',
            );

            setCreateMonitorModal(
              false,
            );

            setEditMonitorModal(
              false,
            );

            setSelectedMonitor(
              null,
            );

            monitorForm.resetForm();

            await Promise.all([
              fetchEndpointMonitors(),
              fetchEndpointMonitoringPlan(),
            ]);
          } catch (error) {
            toast.error(
              error?.response
                ?.data
                ?.statusmessage ||
                error?.response
                  ?.data
                  ?.message ||
                error?.message ||
                'Unable to save endpoint monitor',
            );
          } finally {
            setMonitorSubmitting(
              false,
            );
          }
        },
    });

  // ============================================================
  // CREATE
  // ============================================================

  const handleOpenCreateModal =
    () => {
      if (
        hasReachedEndpointLimit
      ) {
        toast.info(
          'You have reached the endpoint limit for your current plan.',
        );

        return;
      }

      setSelectedMonitor(
        null,
      );

      monitorForm.resetForm({
        values: {
          check_type:
            'http',

          target: '',

          port: 443,

          expected_status_code:
            200,

          check_interval_seconds:
            minimumCheckInterval,

          failure_threshold:
            2,

          notify_email:
            '',

          email_ccs:
            '',

          is_enabled:
            true,
        },
      });

      setCreateMonitorModal(
        true,
      );
    };

  // ============================================================
  // MANAGE MONITOR
  // ============================================================

  const handleOpenManageMonitor =
    (monitor) => {
      setSelectedMonitor(
        monitor,
      );

      setManageMonitorModal(
        true,
      );
    };

  const handleCloseManageMonitor =
    () => {
      setManageMonitorModal(
        false,
      );

      setSelectedMonitor(
        null,
      );
    };

  // ============================================================
  // EDIT
  // ============================================================

  const handleOpenEditModal =
    (monitor) => {
      setSelectedMonitor(
        monitor,
      );

      setEditMonitorModal(
        true,
      );
    };

  // ============================================================
  // CLOSE MONITOR FORM
  // ============================================================

  const handleCloseMonitorModal =
    () => {
      if (
        monitorSubmitting
      ) {
        return;
      }

      setCreateMonitorModal(
        false,
      );

      setEditMonitorModal(
        false,
      );

      setSelectedMonitor(
        null,
      );

      monitorForm.resetForm();
    };

  // ============================================================
  // DELETE
  // ============================================================

  const handleOpenDeleteModal =
    (monitor) => {
      setDeleteMonitorId(
        monitor?.id,
      );

      setSelectedMonitor(
        monitor,
      );

      setDeleteMonitorModal(
        true,
      );
    };

  const handleDeleteMonitor =
    async () => {
      if (
        !projectId ||
        !deleteMonitorId
      ) {
        return;
      }

      try {
        setDeleteLoading(
          true,
        );

        const response =
          await apiEndPoints.deleteEndpointMonitor(
            projectId,
            deleteMonitorId,
          );

        const data =
          unwrapResponse(
            response,
          );

        if (
          data?.statuscode &&
          data.statuscode !==
            '00'
        ) {
          toast.error(
            data?.statusmessage ||
              data?.message ||
              'Unable to delete endpoint monitor',
          );

          return;
        }

        toast.success(
          'Endpoint monitor deleted successfully',
        );

        setDeleteMonitorModal(
          false,
        );

        setDeleteMonitorId(
          null,
        );

        setSelectedMonitor(
          null,
        );

        await Promise.all([
          fetchEndpointMonitors(),
          fetchEndpointMonitoringPlan(),
        ]);
      } catch (error) {
        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            'Unable to delete endpoint monitor',
        );
      } finally {
        setDeleteLoading(
          false,
        );
      }
    };

  // ============================================================
  // ENABLE / DISABLE
  // ============================================================

  const handleToggleMonitor =
    async (monitor) => {
      if (
        !projectId ||
        !monitor?.id
      ) {
        return;
      }

      try {
        setToggleLoadingId(
          monitor.id,
        );

        const response =
          await apiEndPoints.updateEndpointMonitor(
            projectId,
            monitor.id,
            {
              is_enabled:
                !monitor.is_enabled,
            },
          );

        const data =
          unwrapResponse(
            response,
          );

        if (
          data?.statuscode &&
          data.statuscode !==
            '00'
        ) {
          toast.error(
            data?.statusmessage ||
              data?.message ||
              'Unable to update monitor',
          );

          return;
        }

        toast.success(
          monitor.is_enabled
            ? 'Endpoint monitor disabled'
            : 'Endpoint monitor enabled',
        );

        await fetchEndpointMonitors();
      } catch (error) {
        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            'Unable to update endpoint monitor',
        );
      } finally {
        setToggleLoadingId(
          null,
        );
      }
    };

  // ============================================================
  // PLAN MODAL
  // ============================================================

  const handleOpenPlanModal =
    (plan) => {
      setSelectedPlan(
        plan,
      );

      setSelectedPaymentMode(
        currentPaymentMode,
      );

      setChangePlanModal(
        true,
      );
    };

  const handleClosePlanModal =
    () => {
      if (
        planSubmitting
      ) {
        return;
      }

      setChangePlanModal(
        false,
      );

      setSelectedPlan(
        null,
      );

      setSelectedPaymentMode(
        currentPaymentMode,
      );
    };

  // ============================================================
  // CHANGE PLAN
  // ============================================================

  const handleChangePlan =
    async () => {
      if (
        !projectId ||
        !selectedPlan?.slug
      ) {
        return;
      }

      const samePlan =
        selectedPlan?.slug ===
        currentPlan?.slug;

      const samePaymentMode =
        selectedPaymentMode ===
        currentPaymentMode;

      if (
        samePlan &&
        samePaymentMode
      ) {
        toast.info(
          `You are already on the ${selectedPlan.name} ${selectedPaymentMode} plan.`,
        );

        return;
      }

      try {
        setPlanSubmitting(
          true,
        );

        const response =
          await apiEndPoints.changeEndpointMonitorPlan(
            projectId,
            {
              plan_slug:
                selectedPlan.slug,

              payment_mode:
                selectedPaymentMode,
            },
          );

        const data =
          unwrapResponse(
            response,
          );

        if (
          data?.statuscode &&
          data.statuscode !==
            '00'
        ) {
          toast.error(
            data?.statusmessage ||
              data?.message ||
              'Unable to change endpoint monitoring plan',
          );

          return;
        }

        toast.success(
          data?.statusmessage ||
            data?.message ||
            'Endpoint monitoring plan updated successfully',
        );

        setChangePlanModal(
          false,
        );

        setSelectedPlan(
          null,
        );

        await Promise.all([
          fetchEndpointMonitoringPlan(),
          fetchEndpointMonitors(),
        ]);
      } catch (error) {
        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            'Unable to change endpoint monitoring plan',
        );
      } finally {
        setPlanSubmitting(
          false,
        );
      }
    };

  // ============================================================
  // PAGE
  // ============================================================

  return (
    <div className="grid gap-6 p-4 md:p-8 dark:text-white">

      {/* ===================================================== */}
      {/* HEADER */}
      {/* ===================================================== */}

      <div className="flex flex-col gap-2">

        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          Endpoint Monitoring
        </h1>

        <p className="text-sm text-gray-500 dark:text-gray-400">
          Monitor the health and
          availability of your
          project endpoints.
        </p>

      </div>

      {/* ===================================================== */}
      {/* CURRENT PLAN */}
      {/* ===================================================== */}

      <div className="rounded-md border border-[#c4c4c432] bg-white p-5 dark:bg-[#141D26]">

        {planLoading ? (

          <div className="py-10 text-center">

            <p className="text-sm text-gray-500">
              Loading endpoint
              monitoring plan...
            </p>

          </div>

        ) : !currentPlan ? (

          <div className="py-10 text-center">

            <p className="text-sm text-gray-500">
              Endpoint monitoring
              plan information is
              unavailable.
            </p>

          </div>

        ) : (

          <>

            {/* PLAN HEADER */}

            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">

              <div>

                <div className="flex flex-wrap items-center gap-3">

                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                    {currentPlan?.name ||
                      'Current Plan'}
                  </h2>

                  <span className="rounded-full bg-green-100 px-3 py-1 text-[11px] font-medium capitalize text-green-700">
                    {planDetails?.status ||
                      'active'}
                  </span>

                </div>

                <p className="mt-1 text-xs text-gray-500">
                  Your current
                  Endpoint Monitoring
                  subscription.
                </p>

              </div>

              <div className="text-left md:text-right">

                <p className="text-xs uppercase tracking-wide text-gray-400">
                  Endpoint Usage
                </p>

                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                  {endpointsUsed} /{' '}
                  {endpointLimit}
                </p>

              </div>

            </div>

            {/* USAGE */}

            <div className="mt-5">

              <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">

                <span className="text-xs text-gray-500">
                  Endpoint usage
                </span>

                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {endpointsRemaining}{' '}
                  endpoint
                  {endpointsRemaining ===
                  1
                    ? ''
                    : 's'}{' '}
                  remaining
                </span>

              </div>

              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700">

                <div
                  className="h-full rounded-full bg-primaryBlue transition-all"
                  style={{
                    width: `${usagePercentage}%`,
                  }}
                />

              </div>

            </div>

            {/* FIRST ROW */}

            <div className="mt-6 grid gap-4 md:grid-cols-3">

              <PlanInfoCard
                label="Endpoint Limit"
                value={
                  endpointLimit
                }
              />

              <PlanInfoCard
                label="Minimum Check Interval"
                value={`${minimumCheckInterval} seconds`}
              />

              <PlanInfoCard
                label="History Retention"
                value={`${Number(
                  currentPlan?.history_retention_days ||
                    0,
                )} days`}
              />

            </div>

            {/* SECOND ROW */}

            <div className="mt-4 grid gap-4 md:grid-cols-3">

              <PlanInfoCard
                label="Billing"
                value={
                  currentBillingName
                }
              />

              <PlanInfoCard
                label={
                  currentPriceLabel
                }
                value={
                  currentPriceValue
                }
              />

              <PlanInfoCard
                label="Next Subscription Date"
                value={
                  planDetails?.next_sub_date ||
                  'N/A'
                }
              />

            </div>

          </>

        )}

      </div>

      {/* ===================================================== */}
      {/* ENDPOINT MONITORS */}
      {/* ===================================================== */}

      <div className="rounded-md border border-[#c4c4c432] bg-white dark:bg-[#141D26]">

        {/* MONITOR HEADER */}

        <div className="flex flex-col gap-4 border-b border-[#c4c4c432] p-5 sm:flex-row sm:items-center sm:justify-between">

          <div>

            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              Endpoint Monitors
            </h2>

            <p className="mt-1 text-xs text-gray-500">
              Create and manage
              endpoint health checks
              for this project.
            </p>

          </div>

          <button
            type="button"
            disabled={
              hasReachedEndpointLimit
            }
            onClick={
              handleOpenCreateModal
            }
            className={`rounded-md px-5 py-2.5 text-xs font-medium transition-all ${
              hasReachedEndpointLimit
                ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                : 'bg-primaryBlue text-white hover:opacity-90'
            }`}
          >
            Create Endpoint
          </button>

        </div>

        {/* MONITOR CONTENT */}

        {monitorsLoading ? (

          <div className="p-10 text-center">

            <p className="text-sm text-gray-500">
              Loading endpoint
              monitors...
            </p>

          </div>

        ) : monitors.length ===
          0 ? (

          <div className="p-10 text-center">

            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              No endpoint monitors
              found
            </p>

            <p className="mt-1 text-xs text-gray-500">
              Create an endpoint
              monitor to start
              tracking availability.
            </p>

          </div>

        ) : (

          <div className="overflow-x-auto">

            <table className="min-w-full">

              <thead className="bg-gray-50 dark:bg-slate-800">

                <tr>

                  <TableHeader>
                    Endpoint
                  </TableHeader>

                  <TableHeader>
                    Type
                  </TableHeader>

                  <TableHeader>
                    Interval
                  </TableHeader>

                  <TableHeader>
                    Status
                  </TableHeader>

                  <TableHeader>
                    Enabled
                  </TableHeader>

                  <TableHeader>
                    Last Checked
                  </TableHeader>

                  <TableHeader>
                    Actions
                  </TableHeader>

                </tr>

              </thead>

              <tbody className="divide-y divide-[#c4c4c432]">

                {monitors.map(
                  (
                    monitor,
                  ) => (

                    <tr
                      key={
                        monitor.id
                      }
                      className="hover:bg-gray-50 dark:hover:bg-slate-800/50"
                    >

                      <td className="max-w-[280px] px-5 py-4">

                        <p
                          className="truncate text-sm font-medium text-gray-900 dark:text-white"
                          title={
                            monitor?.target
                          }
                        >
                          {monitor?.target ||
                            'N/A'}
                        </p>

                        <p className="mt-1 text-xs text-gray-400">
                          ID:{' '}
                          {monitor?.id}
                        </p>

                      </td>

                      <td className="px-5 py-4 text-sm uppercase text-gray-700 dark:text-gray-300">
                        {monitor?.check_type ||
                          'N/A'}
                      </td>

                      <td className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">
                        {monitor?.check_interval_seconds
                          ? `${monitor.check_interval_seconds}s`
                          : 'N/A'}
                      </td>

                      <td className="px-5 py-4">

                        <StatusBadge
                          status={
                            monitor?.status
                          }
                        />

                      </td>

                      <td className="px-5 py-4">

                        <EnabledBadge
                          enabled={
                            monitor?.is_enabled
                          }
                        />

                      </td>

                      <td className="whitespace-nowrap px-5 py-4 text-xs text-gray-500">
                        {monitor?.last_checked_at ||
                          'N/A'}
                      </td>

                      {/* ===================================== */}
                      {/* SINGLE MANAGE ACTION */}
                      {/* ===================================== */}

                      <td className="px-5 py-4">

                        <button
                          type="button"
                          onClick={() =>
                            handleOpenManageMonitor(
                              monitor,
                            )
                          }
                          className="bg-primaryBlue text-white py-2 px-4 rounded-md text-xs hover:opacity-90 transition-all"
                        >
                          Manage
                        </button>

                      </td>

                    </tr>

                  ),
                )}

              </tbody>

            </table>

          </div>

        )}

      </div>

      {/* ===================================================== */}
      {/* AVAILABLE PLANS */}
      {/* ===================================================== */}

      <div className="rounded-md border border-[#c4c4c432] bg-white p-5 dark:bg-[#141D26]">

        <div className="mb-5">

          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            Available Plans
          </h2>

          <p className="mt-1 text-xs text-gray-500">
            Select a plan and
            billing cycle that
            best suits your
            monitoring
            requirements.
          </p>

        </div>

        {planLoading ? (

          <div className="py-10 text-center">

            <p className="text-sm text-gray-500">
              Loading plans...
            </p>

          </div>

        ) : availablePlans.length ===
          0 ? (

          <div className="py-10 text-center">

            <p className="text-sm text-gray-500">
              No monitoring plans
              are available.
            </p>

          </div>

        ) : (

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">

            {availablePlans.map(
              (plan) => {
                const isCurrentPlan =
                  currentPlan?.slug ===
                  plan?.slug;

                const monthlyPrice =
                  formatPlanPrice(
                    plan,
                    'monthly',
                  );

                const yearlyPrice =
                  formatPlanPrice(
                    plan,
                    'yearly',
                  );

                return (
                  <div
                    key={
                      plan.id ||
                      plan.slug
                    }
                    className={`rounded-md border p-5 ${
                      isCurrentPlan
                        ? 'border-primaryBlue bg-primaryBlue/5'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >

                    <div className="flex items-start justify-between gap-3">

                      <div>

                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {plan.name}
                        </h3>

                        <p className="mt-1 text-xs text-gray-500">
                          Up to{' '}
                          {
                            plan.max_endpoints
                          }{' '}
                          endpoints
                        </p>

                      </div>

                      {isCurrentPlan && (

                        <span className="rounded-full bg-primaryBlue/10 px-3 py-1 text-[10px] font-medium text-primaryBlue">
                          Current
                        </span>

                      )}

                    </div>

                    <div className="mt-5 grid gap-3 text-xs">

                      <PlanLine
                        label="Check interval"
                        value={`${plan.min_check_interval_seconds} sec`}
                      />

                      <PlanLine
                        label="History"
                        value={`${plan.history_retention_days} days`}
                      />

                      <PlanLine
                        label={`Monthly ${
                          isDollarWallet
                            ? 'USD'
                            : 'NGN'
                        }`}
                        value={
                          monthlyPrice
                        }
                      />

                      <PlanLine
                        label={`Yearly ${
                          isDollarWallet
                            ? 'USD'
                            : 'NGN'
                        }`}
                        value={
                          yearlyPrice
                        }
                      />

                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        handleOpenPlanModal(
                          plan,
                        )
                      }
                      className={`mt-5 w-full rounded-md border px-4 py-2.5 text-xs font-medium ${
                        isCurrentPlan
                          ? 'border-primaryBlue bg-white text-primaryBlue dark:bg-transparent'
                          : 'border-primaryBlue bg-primaryBlue text-white'
                      }`}
                    >
                      {isCurrentPlan
                        ? 'Manage Plan'
                        : 'Choose Plan'}
                    </button>

                  </div>
                );
              },
            )}

          </div>

        )}

      </div>

      {/* ===================================================== */}
      {/* MANAGE ENDPOINT MONITOR MODAL */}
      {/* ===================================================== */}

      <Modal
        title={`Manage "${
          selectedMonitor?.target ||
          'Endpoint Monitor'
        }"`}
        isVisible={
          manageMonitorModal
        }
        onClose={
          handleCloseManageMonitor
        }
        size="md:w-[30%]"
      >

        {selectedMonitor && (

          <div className="grid gap-1">

            {/* VIEW DETAILS */}

            <button
              type="button"
              onClick={() => {
                const monitorId =
                  selectedMonitor.id;

                setManageMonitorModal(
                  false,
                );

                setSelectedMonitor(
                  null,
                );

                navigate(
                  `/resources/${projectId}/endpoint-monitoring/${monitorId}`,
                );
              }}
              className="flex w-full items-center justify-between border-b border-[#c4c4c424] px-3 py-4 text-left text-sm font-medium text-black transition-all hover:rounded hover:bg-primaryBlue/5 dark:text-white"
            >

              <span>
                View Monitor Details
              </span>

              <span className="text-lg text-primaryBlue">
                ›
              </span>

            </button>

            {/* EDIT */}

            <button
              type="button"
              onClick={() => {
                setManageMonitorModal(
                  false,
                );

                setEditMonitorModal(
                  true,
                );
              }}
              className="flex w-full items-center justify-between border-b border-[#c4c4c424] px-3 py-4 text-left text-sm font-medium text-black transition-all hover:rounded hover:bg-primaryBlue/5 dark:text-white"
            >

              <span>
                Edit Monitor
              </span>

              <span className="text-lg text-primaryBlue">
                ›
              </span>

            </button>

            {/* ENABLE / DISABLE */}

            <button
              type="button"
              disabled={
                toggleLoadingId ===
                selectedMonitor.id
              }
              onClick={async () => {
                const monitorToUpdate =
                  selectedMonitor;

                setManageMonitorModal(
                  false,
                );

                await handleToggleMonitor(
                  monitorToUpdate,
                );

                setSelectedMonitor(
                  null,
                );
              }}
              className="flex w-full items-center justify-between border-b border-[#c4c4c424] px-3 py-4 text-left text-sm font-medium text-black transition-all hover:rounded hover:bg-primaryBlue/5 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white"
            >

              <span>
                {toggleLoadingId ===
                selectedMonitor.id
                  ? 'Updating...'
                  : selectedMonitor
                      .is_enabled
                    ? 'Disable Monitor'
                    : 'Enable Monitor'}
              </span>

              <span className="text-lg text-primaryBlue">
                ›
              </span>

            </button>

            {/* DELETE */}

            <button
              type="button"
              onClick={() => {
                const monitorToDelete =
                  selectedMonitor;

                setManageMonitorModal(
                  false,
                );

                handleOpenDeleteModal(
                  monitorToDelete,
                );
              }}
              className="flex w-full items-center justify-between px-3 py-4 text-left text-sm font-medium text-red-500 transition-all hover:rounded hover:bg-red-50 dark:hover:bg-red-500/10"
            >

              <span>
                Delete Monitor
              </span>

              <span className="text-lg">
                ›
              </span>

            </button>

          </div>

        )}

      </Modal>

      {/* ===================================================== */}
      {/* CREATE / EDIT MONITOR MODAL */}
      {/* ===================================================== */}

      <Modal
        title={
          editMonitorModal
            ? 'Edit Endpoint Monitor'
            : 'Create Endpoint Monitor'
        }
        isVisible={
          createMonitorModal ||
          editMonitorModal
        }
        onClose={
          handleCloseMonitorModal
        }
        size="md:w-[50%] lg:w-[45%]"
      >

        <form
          onSubmit={
            monitorForm.handleSubmit
          }
          className="grid gap-5"
        >

          <MonitorFormFields
            form={
              monitorForm
            }
            minimumCheckInterval={
              minimumCheckInterval
            }
          />

          <div className="flex justify-end gap-3 border-t border-[#c4c4c432] pt-4">

            <button
              type="button"
              disabled={
                monitorSubmitting
              }
              onClick={
                handleCloseMonitorModal
              }
              className="rounded-md border border-gray-300 px-5 py-2.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={
                monitorSubmitting
              }
              className="rounded-md bg-primaryBlue px-5 py-2.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {monitorSubmitting
                ? 'Saving...'
                : editMonitorModal
                  ? 'Update Monitor'
                  : 'Create Monitor'}
            </button>

          </div>

        </form>

      </Modal>

      {/* ===================================================== */}
      {/* DELETE MODAL */}
      {/* ===================================================== */}

      <Modal
        title="Delete Endpoint Monitor"
        isVisible={
          deleteMonitorModal
        }
        onClose={() => {
          if (
            deleteLoading
          ) {
            return;
          }

          setDeleteMonitorModal(
            false,
          );

          setDeleteMonitorId(
            null,
          );

          setSelectedMonitor(
            null,
          );
        }}
        size="md:w-[35%]"
      >

        <div className="grid gap-5">

          <div>

            <p className="text-sm text-gray-600 dark:text-gray-300">
              Are you sure you want
              to delete this
              endpoint monitor?
            </p>

            {selectedMonitor?.target && (

              <p className="mt-3 break-all rounded-md bg-gray-50 p-3 text-xs font-medium text-gray-700 dark:bg-slate-800 dark:text-gray-300">
                {
                  selectedMonitor.target
                }
              </p>

            )}

          </div>

          <div className="flex justify-end gap-3">

            <button
              type="button"
              disabled={
                deleteLoading
              }
              onClick={() => {
                setDeleteMonitorModal(
                  false,
                );

                setDeleteMonitorId(
                  null,
                );

                setSelectedMonitor(
                  null,
                );
              }}
              className="rounded-md border border-gray-300 px-5 py-2.5 text-sm text-gray-600 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={
                deleteLoading
              }
              onClick={
                handleDeleteMonitor
              }
              className="rounded-md bg-red-500 px-5 py-2.5 text-sm text-white disabled:opacity-50"
            >
              {deleteLoading
                ? 'Deleting...'
                : 'Delete'}
            </button>

          </div>

        </div>

      </Modal>

      {/* ===================================================== */}
      {/* CHANGE PLAN MODAL */}
      {/* ===================================================== */}

      <Modal
        title="Endpoint Monitoring Plan"
        isVisible={
          changePlanModal
        }
        onClose={
          handleClosePlanModal
        }
        size="md:w-[40%]"
      >

        {selectedPlan && (

          <div className="grid gap-6">

            <div>

              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {
                  selectedPlan.name
                }
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Up to{' '}
                {
                  selectedPlan.max_endpoints
                }{' '}
                endpoints
              </p>

            </div>

            {/* BILLING CYCLE */}

            <div>

              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-500">
                Billing Cycle
              </p>

              <div className="grid gap-3 sm:grid-cols-2">

                <button
                  type="button"
                  onClick={() =>
                    setSelectedPaymentMode(
                      'monthly',
                    )
                  }
                  className={`rounded-md border p-4 text-left transition-all ${
                    selectedPaymentMode ===
                    'monthly'
                      ? 'border-primaryBlue bg-primaryBlue/5'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >

                  <p className="text-sm font-medium">
                    Monthly
                  </p>

                  <p className="mt-1 text-lg font-semibold text-primaryBlue">
                    {formatPlanPrice(
                      selectedPlan,
                      'monthly',
                    )}
                  </p>

                </button>

                <button
                  type="button"
                  onClick={() =>
                    setSelectedPaymentMode(
                      'yearly',
                    )
                  }
                  className={`rounded-md border p-4 text-left transition-all ${
                    selectedPaymentMode ===
                    'yearly'
                      ? 'border-primaryBlue bg-primaryBlue/5'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >

                  <p className="text-sm font-medium">
                    Yearly
                  </p>

                  <p className="mt-1 text-lg font-semibold text-primaryBlue">
                    {formatPlanPrice(
                      selectedPlan,
                      'yearly',
                    )}
                  </p>

                </button>

              </div>

            </div>

            {/* SELECTED SUMMARY */}

            <div className="rounded-md bg-gray-50 p-4 dark:bg-slate-800">

              <p className="text-xs uppercase tracking-wide text-gray-400">
                Selected Subscription
              </p>

              <div className="mt-3 flex items-center justify-between gap-4">

                <div>

                  <p className="text-sm font-medium">
                    {
                      selectedPlan.name
                    }{' '}
                    {selectedPaymentMode ===
                    'yearly'
                      ? 'Yearly'
                      : 'Monthly'}
                  </p>

                  <p className="mt-1 text-xs text-gray-500">
                    {
                      selectedPlan.max_endpoints
                    }{' '}
                    endpoint limit
                  </p>

                </div>

                <p className="text-lg font-semibold text-primaryBlue">
                  {formatPlanPrice(
                    selectedPlan,
                    selectedPaymentMode,
                  )}
                </p>

              </div>

            </div>

            <div className="flex justify-end gap-3 border-t border-[#c4c4c432] pt-4">

              <button
                type="button"
                disabled={
                  planSubmitting
                }
                onClick={
                  handleClosePlanModal
                }
                className="rounded-md border border-gray-300 px-5 py-2.5 text-sm text-gray-600 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={
                  planSubmitting
                }
                onClick={
                  handleChangePlan
                }
                className="rounded-md bg-primaryBlue px-5 py-2.5 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {planSubmitting
                  ? 'Updating...'
                  : selectedPlan?.slug ===
                        currentPlan?.slug
                    ? 'Update Plan'
                    : 'Change Plan'}
              </button>

            </div>

          </div>

        )}

      </Modal>

    </div>
  );
};

// ============================================================
// PLAN INFO CARD
// ============================================================

const PlanInfoCard = ({
  label,
  value,
}) => (
  <div className="rounded-md bg-gray-50 p-4 dark:bg-slate-800">

    <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
      {label}
    </p>

    <p className="mt-2 break-words text-sm font-semibold text-gray-900 dark:text-white">
      {value}
    </p>

  </div>
);

// ============================================================
// PLAN LINE
// ============================================================

const PlanLine = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between gap-4">

    <span className="text-gray-500">
      {label}
    </span>

    <span className="text-right font-medium text-gray-800 dark:text-gray-200">
      {value}
    </span>

  </div>
);

// ============================================================
// TABLE HEADER
// ============================================================

const TableHeader = ({
  children,
}) => (
  <th className="whitespace-nowrap px-5 py-4 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
    {children}
  </th>
);

// ============================================================
// MONITOR FORM
// ============================================================

const MonitorFormFields = ({
  form,
  minimumCheckInterval,
}) => (
  <div className="grid gap-5">

    {/* CHECK TYPE */}

    <div>

      <label
        htmlFor="check_type"
        className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-300"
      >
        Check Type
      </label>

      <select
        id="check_type"
        name="check_type"
        value={
          form.values.check_type
        }
        onChange={
          form.handleChange
        }
        onBlur={
          form.handleBlur
        }
        className="w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-primaryBlue dark:border-gray-700 dark:bg-slate-800"
      >

        <option value="http">
          HTTP
        </option>

      </select>

    </div>

    {/* TARGET */}

    <FormField
      label="Endpoint URL"
      name="target"
      type="text"
      placeholder="https://example.com/health"
      value={
        form.values.target
      }
      onChange={
        form.handleChange
      }
      onBlur={
        form.handleBlur
      }
      error={
        form.touched.target &&
        form.errors.target
      }
    />

    <div className="grid gap-5 sm:grid-cols-2">

      <FormField
        label="Port"
        name="port"
        type="number"
        value={
          form.values.port
        }
        onChange={
          form.handleChange
        }
        onBlur={
          form.handleBlur
        }
        error={
          form.touched.port &&
          form.errors.port
        }
      />

      <FormField
        label="Expected Status Code"
        name="expected_status_code"
        type="number"
        value={
          form.values
            .expected_status_code
        }
        onChange={
          form.handleChange
        }
        onBlur={
          form.handleBlur
        }
        error={
          form.touched
            .expected_status_code &&
          form.errors
            .expected_status_code
        }
      />

    </div>

    <div className="grid gap-5 sm:grid-cols-2">

      <FormField
        label={`Check Interval (minimum ${minimumCheckInterval}s)`}
        name="check_interval_seconds"
        type="number"
        min={
          minimumCheckInterval
        }
        value={
          form.values
            .check_interval_seconds
        }
        onChange={
          form.handleChange
        }
        onBlur={
          form.handleBlur
        }
        error={
          form.touched
            .check_interval_seconds &&
          form.errors
            .check_interval_seconds
        }
      />

      <FormField
        label="Failure Threshold"
        name="failure_threshold"
        type="number"
        min="1"
        value={
          form.values
            .failure_threshold
        }
        onChange={
          form.handleChange
        }
        onBlur={
          form.handleBlur
        }
        error={
          form.touched
            .failure_threshold &&
          form.errors
            .failure_threshold
        }
      />

    </div>

    <FormField
      label="Notification Email"
      name="notify_email"
      type="email"
      placeholder="user@example.com"
      value={
        form.values
          .notify_email
      }
      onChange={
        form.handleChange
      }
      onBlur={
        form.handleBlur
      }
      error={
        form.touched
          .notify_email &&
        form.errors
          .notify_email
      }
    />

    <FormField
      label="CC Email"
      name="email_ccs"
      type="email"
      placeholder="team@example.com"
      value={
        form.values.email_ccs
      }
      onChange={
        form.handleChange
      }
      onBlur={
        form.handleBlur
      }
      error={
        form.touched
          .email_ccs &&
        form.errors
          .email_ccs
      }
    />

    <label className="flex cursor-pointer items-center gap-3">

      <input
        type="checkbox"
        name="is_enabled"
        checked={
          form.values
            .is_enabled
        }
        onChange={
          form.handleChange
        }
        className="h-4 w-4 accent-primaryBlue"
      />

      <div>

        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Enable Monitoring
        </p>

        <p className="text-xs text-gray-500">
          Start checking this
          endpoint after it is
          saved.
        </p>

      </div>

    </label>

  </div>
);

// ============================================================
// FORM FIELD
// ============================================================

const FormField = ({
  label,
  error,
  ...props
}) => (
  <div>

    <label
      htmlFor={
        props.name
      }
      className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-300"
    >
      {label}
    </label>

    <input
      {...props}
      id={
        props.name
      }
      className={`w-full rounded-md border bg-white px-4 py-3 text-sm outline-none focus:border-primaryBlue dark:bg-slate-800 ${
        error
          ? 'border-red-400'
          : 'border-gray-300 dark:border-gray-700'
      }`}
    />

    {error && (

      <p className="mt-1 text-xs text-red-500">
        {error}
      </p>

    )}

  </div>
);

// ============================================================
// STATUS BADGE
// ============================================================

const StatusBadge = ({
  status,
}) => {
  const value =
    String(
      status || 'unknown',
    ).toLowerCase();

  if (
    value === 'up' ||
    value === 'healthy' ||
    value === 'active'
  ) {
    return (
      <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-medium capitalize text-green-700">
        {value}
      </span>
    );
  }

  if (
    value === 'down' ||
    value === 'failed' ||
    value === 'unhealthy' ||
    value === 'error'
  ) {
    return (
      <span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-medium capitalize text-red-700">
        {value}
      </span>
    );
  }

  return (
    <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-medium capitalize text-amber-700">
      {value}
    </span>
  );
};

// ============================================================
// ENABLED BADGE
// ============================================================

const EnabledBadge = ({
  enabled,
}) =>
  enabled ? (
    <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
      Enabled
    </span>
  ) : (
    <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
      Disabled
    </span>
  );

export default EndpointMonitoring;